import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { BarChart } from '@/components/charts/BarChart';
import { StatWidget } from '@/components/widgets/StatWidget';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { BsTooltip } from '@/components/common/BsTooltip';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import { http } from '@/api/client/httpClient';
import { formatNumber } from '@/utils/format';
import { analyticsApi } from '@/api/services/analytics';
import {
  AnalyticsFilterBar,
  defaultAnalyticsFilters,
  toAnalyticsInput,
  type AnalyticsFilterState,
} from './AnalyticsFilterBar';
import { AnalyticsPermissions as P } from './permissions';
import type {
  AnalyticsResultDto,
  ConsolidatedAnalyticsResultDto,
  ConsolidatedTraderDto,
  CountryPerformanceDto,
  TraderPerformanceDto,
} from '@/api/types/analytics';

const newGuid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`);
const num = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? 'N/A' : formatNumber(v, d));

const PNL_TOOLTIP = 'Accounts For The Unrealised PnL Of The Beginning And Ending date';
const pnlHeader = (label: string) => <BsTooltip title={PNL_TOOLTIP}>{label}</BsTooltip>;

export function AnalyticsPage() {
  const toast = useToast();
  const can = usePermission();
  const [draft, setDraft] = useState<AnalyticsFilterState>(defaultAnalyticsFilters);
  const [applied, setApplied] = useState<AnalyticsFilterState>(draft);
  const [guid, setGuid] = useState(newGuid);
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);

  const input = () => toAnalyticsInput(applied, guid);
  const perf = useQuery<AnalyticsResultDto | ConsolidatedAnalyticsResultDto>({
    queryKey: ['an-perf', applied, guid],
    queryFn: () => (applied.byUniqueId ? analyticsApi.calculateByUniqueId(input()) : analyticsApi.calculateByLogin(input())),
  });
  const dtf = useQuery({ queryKey: ['an-dtf', applied, guid], queryFn: () => analyticsApi.getDtfTable(input()) });
  const volChart = useQuery({ queryKey: ['an-vol', applied, guid], queryFn: () => analyticsApi.getTop8ByVolume(input()) });
  const notChart = useQuery({ queryKey: ['an-not', applied, guid], queryFn: () => analyticsApi.getTop8ByNotional(input()) });

  const uid = applied.byUniqueId;
  const loginRes = !uid ? (perf.data as AnalyticsResultDto | undefined) : undefined;
  const uidRes = uid ? (perf.data as ConsolidatedAnalyticsResultDto | undefined) : undefined;
  const cp = perf.data?.clientsPerformance;

  const relatedCount = useMemo(() => {
    const keys = new Set<string>();
    if (uid) {
      (uidRes?.topXTraders ?? []).forEach((r) => keys.add(r.uniqueId));
      (uidRes?.worstXTraders ?? []).forEach((r) => keys.add(r.uniqueId));
      (dtf.data?.uniqueIdRows ?? []).forEach((r) => keys.add(r.uniqueId));
    } else {
      const add = (l: number, p: number) => keys.add(`${l}:${p}`);
      (loginRes?.topXTraders ?? []).forEach((r) => add(r.login, r.platformId));
      (loginRes?.worstXTraders ?? []).forEach((r) => add(r.login, r.platformId));
      (dtf.data?.loginRows ?? []).forEach((r) => add(r.login, r.platformId));
    }
    return keys.size;
  }, [uid, uidRes, loginRes, dtf.data]);

  const fetch = () => {
    setApplied({ ...draft });
    setGuid(newGuid());
  };

  const onModeToggle = (key: 'byUniqueId' | 'live' | 'showLP', value: boolean) => {
    if (key === 'live') {
      const today = new Date();
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const newEnd = value ? fmt(today) : fmt(y);
      setDraft((f) => ({ ...f, live: value, endDate: newEnd, begDate: f.begDate > newEnd ? newEnd : f.begDate }));
      return;
    }
    if (key === 'byUniqueId') {
      localStorage.setItem('AnalyticsByUniqueId', String(value));
      setDraft((f) => ({ ...f, byUniqueId: value, specificLogin: null, connectorId: value ? null : f.connectorId, portfolioProfile: value ? null : f.portfolioProfile }));
      return;
    }
    setDraft((f) => ({ ...f, [key]: value }));
  };

  async function extract() {
    try {
      const res = await http.post(analyticsApi.exportToExcelUrl, input(), { responseType: 'blob' });
      const fileName = uid ? 'Consolidated Traders Analytics.xlsx' : 'Traders Analytics.xlsx';
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export analytics.');
    }
  }

  // Warning surfaced once per result.
  const warning = perf.data?.warning;
  useEffect(() => {
    if (warning) toast.info(warning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warning]);

  const loginPnlCell = (r: TraderPerformanceDto | ConsolidatedTraderDto) =>
    r.isLp && r.isMissingLpSnapshot ? <span className="text-muted">NA</span> : <span>{num(r.pnl)}</span>;

  const loginLink = (login: number, platformId: number) => (
    <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); if (platformId) setLoModal({ login, connector: platformId }); }}>{login}</a>
  );

  const traderLoginCols: DataGridColumn<TraderPerformanceDto>[] = [
    { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login, cell: (r) => loginLink(r.login, r.platformId) },
    { key: 'pnl', title: pnlHeader('PnL'), className: 'text-end', value: (r) => r.pnl ?? null, cell: (r) => loginPnlCell(r) },
    { key: 'netInOut', title: 'Net In Out', className: 'text-end', value: (r) => r.netInOut ?? null, cell: (r) => num(r.netInOut) },
    { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
    { key: 'platform', title: 'Platform', className: 'text-start', value: (r) => r.platform },
  ];
  const traderUidCols: DataGridColumn<ConsolidatedTraderDto>[] = [
    { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId },
    { key: 'pnl', title: pnlHeader('PnL'), className: 'text-end', value: (r) => r.pnl ?? null, cell: (r) => loginPnlCell(r) },
    { key: 'netInOut', title: 'Net In/Out', className: 'text-end', value: (r) => r.netInOut ?? null, cell: (r) => num(r.netInOut) },
    { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
  ];
  const countryCols: DataGridColumn<CountryPerformanceDto>[] = [
    { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
    { key: 'nbOfClients', title: 'Nb Of Clients', className: 'text-end', value: (r) => r.nbOfClients, cell: (r) => num(r.nbOfClients, 0) },
    { key: 'totalPnL', title: pnlHeader('Total PnL'), className: 'text-end', value: (r) => r.totalPnL ?? null, cell: (r) => num(r.totalPnL) },
  ];

  const widgets: { label: string; value: string; icon: string; theme: 'neutral' | 'primary' | 'success' | 'danger' | 'warning' | 'info' }[] = [
    { label: 'Total PNL', value: num(cp?.totalPnL), icon: 'fa-solid fa-square-poll-vertical', theme: 'warning' },
    { label: 'Clients PNL', value: num(cp?.clientsPnL), icon: 'fa-solid fa-square-poll-vertical', theme: 'info' },
    { label: 'LP PNL', value: cp?.lpPnLMissing ? 'N/A' : num(cp?.lpPnL), icon: 'fa-solid fa-square-poll-vertical', theme: 'primary' },
    { label: 'Losing Clients %', value: num(cp?.losingClientsPercentage), icon: 'fa-sharp fa-solid fa-check-to-slot', theme: 'danger' },
    { label: 'Notional Traded Volume', value: num(dtf.data?.totalDtf), icon: 'fa-solid fa-file-invoice-dollar', theme: 'neutral' },
    { label: uid ? 'Related Unique Ids' : 'Related Logins', value: String(relatedCount), icon: 'fa-solid fa-user', theme: 'neutral' },
  ];

  const countryRows = (loginRes?.countryPerformance ?? uidRes?.countryPerformance ?? []) as CountryPerformanceDto[];

  return (
    <div className="analytics-page">
      <PageHeader title="Analytics" breadcrumb={['Home', 'Analytics']} />

      <AnalyticsFilterBar draft={draft} setDraft={setDraft} onFetch={fetch} onExtract={extract} onModeToggle={onModeToggle} canExcel={can(P.excel)} fetching={perf.isFetching} />

      <div className="row g-2 mb-3 analytics-counters">
        {widgets.map((w) => (
          <div className="col-6 col-sm-4 col-md-4 col-lg-2" key={w.label}>
            <StatWidget label={w.label} value={w.value} icon={w.icon} theme={w.theme} />
          </div>
        ))}
      </div>

      <div className="row g-2 mb-3">
        <div className="col-lg-4">
          <div className="card custom-card mb-0 h-100">
            <div className="card-header border-bottom"><h6 className="card-title text-center w-100 mb-0">Top 8 Instruments By Volume Closed</h6></div>
            <div className="card-body" style={{ minHeight: 400 }}>
              <BarChart
                height={360}
                labels={volChart.data?.labels ?? []}
                series={[{ label: 'Volume Closed in Lots', data: volChart.data?.datasets?.[0]?.data ?? [] }]}
                emptyHint="No data"
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card custom-card mb-0 h-100">
            <div className="card-header border-bottom"><h6 className="card-title text-center w-100 mb-0">Top 8 Instruments By Notional Value Traded</h6></div>
            <div className="card-body" style={{ minHeight: 400 }}>
              <BarChart
                height={360}
                labels={notChart.data?.labels ?? []}
                series={[{ label: 'Notional Value Traded (Thousands)', data: notChart.data?.datasets?.[0]?.data ?? [] }]}
                emptyHint="No data"
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          {uid ? (
            <DataGrid
              columns={[
                { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId },
                { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
                { key: 'dtf', title: 'Notional Volume', className: 'text-end', value: (r) => r.dtf, cell: (r) => num(r.dtf, 0) },
              ]}
              data={dtf.data?.uniqueIdRows ?? []}
              rowKey={(r) => r.uniqueId}
              pageSize={5}
              card
              fillHeight
              title="Notional Traded Volume"
              includeExcel
              excelFileName="Notional Traded Volume"
              emptyMessage="No data."
            />
          ) : (
            <DataGrid
              columns={[
                { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login, cell: (r) => loginLink(r.login, r.platformId) },
                { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
                { key: 'dtf', title: 'Notional Volume', className: 'text-end', value: (r) => r.dtf, cell: (r) => num(r.dtf, 0) },
                { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
              ]}
              data={dtf.data?.loginRows ?? []}
              rowKey={(r) => `${r.login}:${r.platformId}`}
              pageSize={5}
              card
              fillHeight
              title="Notional Traded Volume"
              includeExcel
              excelFileName="Notional Traded Volume"
              emptyMessage="No data."
            />
          )}
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-lg-4">
          {uid ? (
            <DataGrid columns={traderUidCols} data={uidRes?.topXTraders ?? []} rowKey={(r) => r.uniqueId} pageSize={5} card fillHeight title="Top Traders" includeExcel excelFileName="Top Traders" emptyMessage="No data." />
          ) : (
            <DataGrid columns={traderLoginCols} data={loginRes?.topXTraders ?? []} rowKey={(r) => `${r.login}:${r.platformId}`} pageSize={5} card fillHeight title="Top Traders" includeExcel excelFileName="Top Traders" emptyMessage="No data." />
          )}
        </div>
        <div className="col-lg-4">
          {uid ? (
            <DataGrid columns={traderUidCols} data={uidRes?.worstXTraders ?? []} rowKey={(r) => r.uniqueId} pageSize={5} card fillHeight title="Worst Traders" includeExcel excelFileName="Worst Traders" emptyMessage="No data." />
          ) : (
            <DataGrid columns={traderLoginCols} data={loginRes?.worstXTraders ?? []} rowKey={(r) => `${r.login}:${r.platformId}`} pageSize={5} card fillHeight title="Worst Traders" includeExcel excelFileName="Worst Traders" emptyMessage="No data." />
          )}
        </div>
        <div className="col-lg-4">
          <DataGrid columns={countryCols} data={countryRows} rowKey={(r) => r.country} pageSize={5} card fillHeight title="Country Performance" includeExcel excelFileName="Country Performance" emptyMessage="No data." />
        </div>
      </div>

      {loModal && <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />}
    </div>
  );
}
